/**
 * Synthetic rail-turn markers (fix/hide-rail-action-synthetic-bubble).
 *
 * The engine persists every onboarding rail-button click as a `role: 'user'`
 * turn so the DB history records which button drove the turn
 * (`axiomgo-engine/src/services/onboarding/onboarding-action-handler.ts` —
 * `describeOnboardingAction`). Two of those forms are machine markers never
 * meant for the user's eyes:
 *
 *   - `Ação da esteira: <actionId>.` — the actionId-only payload (Fatia D),
 *     i.e. EVERY current rail click (`onboarding.kickoff`, `welcome.advance`, ...)
 *   - `Ação de onboarding.`          — the actionId-less fallback
 *
 * The remaining legacy forms ("Meu setor: X.", "Quero configurar o AxiomGO.",
 * ...) read as natural first-person replies and stay visible on replay —
 * decided with Denis 2026-07-22.
 *
 * The predicate is deliberately tight: an actionId is a whitespace-free token
 * with at least one INTERNAL dot (`onboarding.kickoff`, `welcome.advance`,
 * `plan_action.somente_equipamento` — see engine `flows.config.ts`). "esteira"
 * is live domain vocabulary (conveyor belt), so plausible typed prose like
 * "Ação da esteira: parada." must never match — the dotless single token
 * excludes it.
 */

/** Exact fallback marker emitted when the payload carries no `actionId`. */
const FALLBACK_MARKER = 'Ação de onboarding.'

/** `Ação da esteira: <actionId>.` — actionId = dot-separated segments, ≥2. */
const ACTION_ID_MARKER = /^Ação da esteira: [^\s.]+(\.[^\s.]+)+\.$/

/** True when `content` is one of the engine's machine markers for a rail click. */
export function isSyntheticRailTurnContent(content: string): boolean {
  const trimmed = content.trim()
  return trimmed === FALLBACK_MARKER || ACTION_ID_MARKER.test(trimmed)
}

/**
 * Drops synthetic rail-click user turns from an engine history payload before
 * it enters the chat store (ChatPage's `mergeMessages` ingestion). Only
 * `role: 'user'` turns are candidates — an assistant turn is never dropped.
 */
export function stripSyntheticRailTurns<T extends { role: string; content: string }>(
  messages: T[],
): T[] {
  return messages.filter((m) => m.role !== 'user' || !isSyntheticRailTurnContent(m.content))
}
